import type { BoardColumn, ItemHistoryEntry } from '@hugo/core'
import { COLUMN_LABELS } from './board'
import { choiceLabel, eventLabel, formatDue, sourceLabel, triagedLabel } from './format'

const CONNECTOR_ACTOR = 'connector:'

/** Edited fields by the names the details sheet gives them. */
const FIELD_NAMES: Record<string, string> = {
  title: 'title',
  notes: 'notes',
  type: 'type',
  size: 'size',
  tags: 'tags',
  priority: 'priority',
  estimateMinutes: 'time needed',
}

/**
 * Who acted, as an item's history names it: nobody for the user (the default), "hugo" for its
 * agent, the source for a connector ("Calendar" when Calendar closed an answered invitation),
 * else the actor itself.
 */
export function historyActor(actor: string): string | null {
  if (actor === 'user') return null
  if (actor === 'agent') return 'hugo'
  return actor.startsWith(CONNECTOR_ACTOR)
    ? sourceLabel(actor.slice(CONNECTOR_ACTOR.length))
    : actor
}

/** What an item is after its kind changed: who waits on it, not a list it moved to. */
const NOW_LABELS: Record<string, string> = {
  task: 'Now a plain task',
  owe: 'Now I owe',
  waiting: 'Now waiting on',
}

/** What happened: "Filed in I owe", "hugo suggested FYI", "Now I owe", else the event's label. */
export function historyLabel(entry: ItemHistoryEntry): string {
  const choice = entry.data.choice
  if (entry.type === 'item.triaged' && typeof choice === 'string') return triagedLabel(choice)
  if (entry.type === 'item.kind_changed' && typeof entry.data.to === 'string') {
    return NOW_LABELS[entry.data.to] ?? eventLabel(entry.type)
  }
  if (entry.type === 'item.suggested' && typeof choice === 'string') {
    return `hugo suggested ${choiceLabel(choice)}`
  }
  return eventLabel(entry.type)
}

/** The detail after an entry's label, e.g. "done" for "Closed", or null when there is none. */
export function historyDetail(entry: ItemHistoryEntry, timeZone: string): string | null {
  const d = entry.data
  if (entry.type === 'item.due_set') {
    return typeof d.to === 'string' ? formatDue(d.to, d.allDay === true, timeZone) : 'cleared'
  }
  if (entry.type === 'item.snoozed' && typeof d.until === 'string')
    return `until ${formatDue(d.until, false, timeZone)}`
  if (entry.type === 'item.closed' && typeof d.reason === 'string') return d.reason
  if (entry.type === 'item.updated' && d.after && typeof d.after === 'object')
    return Object.keys(d.after)
      .map((field) => FIELD_NAMES[field] ?? field)
      .join(', ')
  if (entry.type === 'item.planned' && typeof d.to === 'string') {
    return `to ${COLUMN_LABELS[d.to as BoardColumn] ?? d.to}`
  }
  if (entry.type === 'item.activity' && typeof d.source === 'string') {
    // The connector is usually also the actor, which is shown anyway: name the source once.
    return entry.actor === `${CONNECTOR_ACTOR}${d.source}` ? null : sourceLabel(d.source)
  }
  if (entry.type === 'outbox.failed' && typeof d.error === 'string') return d.error
  if (entry.type === 'outbox.withdrawn' && typeof d.reason === 'string') return d.reason
  if (entry.type.startsWith('outbox.') && Array.isArray(d.recipients)) {
    return `to ${d.recipients.filter((r) => typeof r === 'string').join(', ')}`
  }
  return null
}

/** One line of an item's history: "Filed in I owe · hugo", "Closed · done · Calendar". */
export function historyLine(
  entry: ItemHistoryEntry,
  timeZone: string,
): { label: string; detail: string | null; actor: string | null } {
  return {
    label: historyLabel(entry),
    detail: historyDetail(entry, timeZone),
    // "hugo suggested …" names its actor already.
    actor: entry.type === 'item.suggested' ? null : historyActor(entry.actor),
  }
}

/** Item events that change nothing about the item (its version), so undo still applies. */
const UNCHANGING = new Set(['item.activity', 'item.suggested', 'item.suggestion_dismissed'])

/**
 * The action to undo hugo's filing with (`action.undo`), while that can still work: the latest
 * filing is hugo's, not undone, and nothing changed the item since. Undo restores the state from
 * before the filing only if the item is unchanged, and undoing a later change changes it again.
 * Events of the filing's own action (the due date it set) and events that change nothing (new
 * messages, suggestions, outbox drafts) do not count. The API still decides (e.g. its time
 * window); null when there is nothing to offer.
 */
export function undoableHugoFiling(history: ItemHistoryEntry[]): string | null {
  const index = history.findLastIndex((e) => e.type === 'item.triaged' && !e.undone)
  const filing = history[index]
  // The API says whether it would still undo it (its window, its rules).
  if (filing?.actor !== 'agent' || !filing.actionId || !filing.undoable) return null
  const changedSince = history
    .slice(index + 1)
    .some(
      (e) =>
        e.actionId !== filing.actionId && e.type.startsWith('item.') && !UNCHANGING.has(e.type),
    )
  return changedSince ? null : filing.actionId
}
